import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMe } from '../../api';

// Loads the signed-in user's profile and permissions once per session (GET
// /api/me) and shares them with the sidebar, the routes, and any page that
// needs to know what the caller may do — including whether the profile is
// limited to certain marketing vendors, so pages can say so.
//
// This drives what the app *shows*. It is not what keeps anyone out: every
// endpoint re-checks the same permission server-side, so a hidden menu item and
// a hand-typed URL are equally ineffective at reaching data you don't have.

const AccessContext = createContext({
    me: null,
    loading: true,
    error: null,
    can: () => false,
    vendorScope: { scoped: false, names: [] },
    reload: () => {},
});

export const useAccess = () => useContext(AccessContext);

// Permission keys, mirroring server/auth/permissions.go.
export const PERMISSIONS = {
    addendum: 'section:addendum',
    marketing: 'section:marketing',
    marketingVendors: 'page:marketing.vendors',
    marketingCampaigns: 'page:marketing.campaigns',
    marketingSpends: 'page:marketing.spends',
    marketingAdSync: 'page:marketing.adsync',
    reports: 'section:reports',
    blocked: 'section:blocked',
    canopy: 'section:canopy',
    commissions: 'section:commissions',
    users: 'section:users',
};

export const reportPermission = (key) => `report:${key}`;

export function AccessProvider({ children }) {
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    // { message, code } — code is 'no-account' or 'inactive' when the server
    // says the sign-in was fine but this person has no usable access.
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setMe(await fetchMe());
        } catch (err) {
            setMe(null);
            setError({ message: err.message, code: err.code });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const granted = useMemo(() => new Set(me?.permissions || []), [me]);

    // Admin profiles hold every permission, including ones added after the
    // profile was set up — the same rule the server applies.
    const can = useCallback(
        (permission) => Boolean(me && (me.is_admin || granted.has(permission))),
        [me, granted],
    );

    // Whether this profile only sees some vendors' data, and which. The server
    // applies the limit to every report and page; this is for saying so.
    const vendorScope = useMemo(
        () => ({ scoped: Boolean(me?.vendor_scoped), names: me?.vendor_names || [] }),
        [me],
    );

    const value = useMemo(
        () => ({ me, loading, error, can, vendorScope, reload: load }),
        [me, loading, error, can, vendorScope, load],
    );

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}
